import React, { useEffect, useState, useCallback } from 'react';
import { apiGet } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { Users, Ticket, UserCheck, Mail, Music, Calendar, Bed, Clock, CheckCircle, XCircle, Table2, Activity, Beer, Image, Wifi, HardDrive, Radio, AlertTriangle, Download } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    apiGet('/api/admin/dashboard').then(r => {
      setStats(r.data.stats);
      setActivity(r.data.activity || []);
    }).catch(() => {}).finally(() => setLoading(false));
    apiGet('/api/health').then(r => setHealth(r.data)).catch(() => setHealth({ status: 'error' }));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <div className="text-slate-400">Indlæser overblik...</div>;
  if (!stats) return <div className="text-red-400">Overblikket kunne ikke indlæses</div>;

  const cards = [
    { label: 'Invitationer', value: stats.total_invitations, icon: Ticket, color: 'text-blue-400 border-blue-500/20' },
    { label: 'Gæster', value: stats.total_guests, icon: Users, color: 'text-purple-400 border-purple-500/20' },
    { label: 'RSVPs', value: stats.total_rsvps, icon: UserCheck, color: 'text-indigo-400 border-indigo-500/20' },
    { label: 'Deltager', value: stats.attending, icon: Calendar, color: 'text-emerald-400 border-emerald-500/20' },
    { label: 'Deltager ikke', value: stats.not_attending, icon: XCircle, color: 'text-red-400 border-red-500/20' },
    { label: 'Checket ind', value: stats.checked_in, icon: CheckCircle, color: 'text-green-400 border-green-500/20' },
    { label: 'Ikke ankommet', value: stats.not_arrived, icon: Clock, color: 'text-amber-400 border-amber-500/20' },
    { label: 'Overnatning', value: stats.overnight_requests, icon: Bed, color: 'text-teal-400 border-teal-500/20' },
    { label: 'Borde', value: stats.total_tables, icon: Table2, color: 'text-cyan-400 border-cyan-500/20' },
    { label: 'Musikønsker', value: stats.music_requests, icon: Music, color: 'text-pink-400 border-pink-500/20' },
    { label: 'Musikkø', value: stats.music_queued, icon: Music, color: 'text-violet-400 border-violet-500/20' },
    { label: 'Barordrer', value: stats.bar_orders, icon: Beer, color: 'text-amber-400 border-amber-500/20' },
    { label: 'Bar klar', value: stats.bar_ready, icon: Beer, color: 'text-emerald-400 border-emerald-500/20' },
    { label: 'Billeder', value: stats.uploads, icon: Image, color: 'text-cyan-400 border-cyan-500/20' },
    { label: 'Mails', value: stats.emails_sent, icon: Mail, color: 'text-sky-400 border-sky-500/20' },
  ];

  const summaryPanels = [
    {
      title: 'Barkø',
      color: 'text-amber-400',
      icon: Beer,
      items: [
        ['Afventer', stats.bar_pending || 0, 'text-amber-400'],
        ['Forberedes', stats.bar_preparing || 0, 'text-blue-400'],
        ['Klar', stats.bar_ready || 0, 'text-emerald-400'],
        ['Leveret', stats.bar_delivered || 0, 'text-slate-400'],
      ],
    },
    {
      title: 'Musik',
      color: 'text-purple-400',
      icon: Radio,
      items: [
        ['Afventer', stats.music_pending || 0, 'text-amber-400'],
        ['Godkendt', stats.music_approved || 0, 'text-emerald-400'],
        ['I kø', stats.music_queued || 0, 'text-blue-400'],
        ['Spillet', stats.music_played || 0, 'text-slate-400'],
      ],
    },
    {
      title: 'RSVP / Check-in',
      color: 'text-emerald-400',
      icon: UserCheck,
      items: [
        ['RSVPs', stats.total_rsvps || 0, 'text-blue-400'],
        ['Deltager', stats.attending || 0, 'text-emerald-400'],
        ['Deltager ikke', stats.not_attending || 0, 'text-red-400'],
        ['Checket ind', stats.checked_in || 0, 'text-green-400'],
      ],
    },
    {
      title: 'Systemstatus',
      color: 'text-cyan-400',
      icon: Wifi,
      items: [
        ['SMTP', stats.smtp_enabled ? 'Til' : 'Fra', stats.smtp_enabled ? 'text-emerald-400' : 'text-amber-400'],
        ['Spotify', stats.spotify_enabled ? 'Til' : 'Fra', stats.spotify_enabled ? 'text-emerald-400' : 'text-amber-400'],
        ['Billedlager', stats.storage_enabled ? 'Til' : 'Lokalt', stats.storage_enabled ? 'text-emerald-400' : 'text-blue-400'],
        ['Mailfejl', stats.emails_failed || 0, stats.emails_failed ? 'text-red-400' : 'text-slate-400'],
      ],
    },
  ];

  const systemWarnings = [
    !stats.smtp_enabled && {
      title: 'SMTP er ikke konfigureret',
      message: 'Invitationer og påmindelser bliver kun logget og sendes ikke til gæsterne.',
    },
    !stats.storage_enabled && {
      title: 'Billeder gemmes lokalt',
      message: 'Lokal billedlagring er aktiv i udviklingsmiljøet.',
    },
  ].filter(Boolean);

  return (
    <div className="animate-fade-in" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Administrativt overblik</h1>
        <div className="flex items-center gap-2">
          {stats.smtp_enabled ? (
            <span className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400">SMTP aktiv</span>
          ) : (
            <span className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-400">SMTP ikke aktiv</span>
          )}
          <span className="text-xs text-slate-500">Opdateres hvert 10. sek.</span>
          <Link to="/admin/export" className="inline-flex items-center gap-1 rounded bg-blue-600/15 px-2 py-1 text-xs text-blue-300 hover:bg-blue-600/25">
            <Download className="w-3 h-3" /> Eksport
          </Link>
          {health && (
            <span className={`text-xs px-2 py-1 rounded ${health.status === 'ok' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              API {health.status}
            </span>
          )}
        </div>
      </div>

      {systemWarnings.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="system-warnings">
          {systemWarnings.map((warning) => (
            <div key={warning.title} className="flex gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-300" />
              <div>
                <p className="text-sm font-semibold text-amber-100">{warning.title}</p>
                <p className="text-sm text-amber-200/80 mt-1">{warning.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {cards.map((c, i) => (
          <div key={c.label} className={`bg-slate-800/40 border ${c.color.split(' ')[1]} rounded-xl p-4 animate-fade-in`}
            style={{ animationDelay: `${i * 0.03}s`, opacity: 0 }} data-testid={`stat-${c.label.toLowerCase().replace(/[\s.]/g, '-')}`}>
            <c.icon className={`w-5 h-5 mb-2 ${c.color.split(' ')[0]}`} />
            <p className="text-2xl font-semibold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        {summaryPanels.map((panel) => (
          <div key={panel.title} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
            <h3 className={`text-xs font-bold uppercase tracking-[0.2em] mb-4 flex items-center gap-2 ${panel.color}`}>
              <panel.icon className="w-3.5 h-3.5" /> {panel.title}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {panel.items.map(([label, value, color]) => (
                <div key={label} className="bg-slate-900/30 rounded-lg p-3">
                  <p className={`text-lg font-semibold tabular-nums ${color}`}>{value}</p>
                  <p className="text-xs text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        {stats.invitation_statuses && (
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-purple-400 mb-4 flex items-center gap-2">
            <HardDrive className="w-3.5 h-3.5" /> Invitationsstatus
          </h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(stats.invitation_statuses).map(([s, c]) => (
                <div key={s} className="text-center p-3 bg-slate-900/30 rounded-lg">
                  <p className="text-lg font-semibold text-slate-100">{c}</p>
                <p className="text-xs text-slate-500">{statusLabel(s)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Feed */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> Seneste aktivitet
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {activity.length === 0 ? (
              <p className="text-sm text-slate-500">Ingen nylig aktivitet</p>
            ) : activity.map((a, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-700/20 last:border-0">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-slate-300">{a.action?.replace(/_/g, ' ')}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{a.actor}</span>
                    {a.details && <span className="truncate">- {a.details}</span>}
                  </div>
                </div>
                <span className="text-xs text-slate-600 flex-shrink-0 ml-auto text-right">
                  <span className="block">{formatRelativeTime(a.timestamp)}</span>
                  <span className="block">{a.timestamp ? new Date(a.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function statusLabel(status) {
  const labels = {
    pending: 'Afventer',
    invited: 'Inviteret',
    opened: 'Åbnet',
    registered: 'Registreret',
    completed: 'Fuldført',
    declined: 'Afvist',
  };
  return labels[status] || status;
}
